// ================================================================
// src/pages/teacher/TeacherBooks.jsx
// ================================================================
// داشبورد معلم — نقطه‌ی ورود همه‌ی فعالیت‌های معلم
// ----------------------------------------------------------------
// مسئولیت‌ها:
//   ۱. Header با هویت معلم + آمار سریع + NotificationBell
//   ۲. Tab: درخواست‌های من (with Wizard برای درخواست جدید)
//   ۳. Tab: پروژه‌های من (grid کارت‌ها + entry به ویرایشگر)
//   ۴. Tab: پروفایل (ویرایش اطلاعات معلم)
//   ۵. Guard: چک نقش + redirect
//   ۶. واکنش‌گرا: موبایل/تبلت/دسکتاپ
// ================================================================

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext.jsx';
import './teacher-books.css';
import '../../components/books/books-shared.css';

import {
  // 🆕 Core engine — برای sync تم با سرور
  engine,
  // Provider
  BooksEngineProvider,
  // Base
  useBooksEngine, usePermission, useEngineReady, useEngineTick,
  // Data
  useTeacherByUserId, useMyRequests, useMyProjects,
  useSystemStats,
  // Actions
  useTeacherRequestActions,
  useProjectActions, useVersionActions,
  // Media
  useImageUpload,
  // Constants
  REQUEST_STATUS, PROJECT_STATE,
  GRADE_TITLES, SUBJECT_LABELS, SUBJECTS_BY_GRADE,
  normalizeGrade, normalizeSubject,
  // Helpers
  useKeyboardShortcuts,
} from '../../hooks/useBooksEngine.js';

import dataEngine from '../../core/books/data.js';

// 🔽 CHANGED: curriculum helpers برای پشتیبانی از پایه ۷-۱۲ و رشته‌های متوسطه دوم
import {
  GRADE_LIST,
  FIELD_LIST,
  getField as getFieldMeta,
  getSubject as getSubjectMeta,
  getSubjectIdsFor,
  needsField as curriculumNeedsField,
  normalizeFieldId as curriculumNormalizeField,
} from '../../core/education/curriculum.js';

import {
  Spinner, Badge, Chip, EmptyState, SkeletonLoader,
  Modal, ConfirmModal, Wizard,
  ImageUploader, BlobImage, NotificationBell,
  VersionBadge, ProjectStateBadge,
} from '../../components/books/BooksSharedUI.jsx';

// ================================================================
// SECTION 1: CONSTANTS
// ================================================================

const TABS = Object.freeze({
  REQUESTS: 'requests',
  PROJECTS: 'projects',
  PROFILE: 'profile',
});

const TAB_LABELS = {
  [TABS.REQUESTS]: 'درخواست‌های من',
  [TABS.PROJECTS]: 'پروژه‌های من',
  [TABS.PROFILE]: 'پروفایل',
};

const REQUEST_STATUS_UI = {
  [REQUEST_STATUS.DRAFT]: { tone: 'neutral', label: 'پیش‌نویس', icon: '📝' },
  [REQUEST_STATUS.PENDING]: { tone: 'warning', label: 'در انتظار بررسی', icon: '⏳' },
  [REQUEST_STATUS.EDIT_REQUESTED]: { tone: 'info', label: 'نیاز به اصلاح', icon: '✏️' },
  [REQUEST_STATUS.REJECTED]: { tone: 'danger', label: 'رد شده', icon: '❌' },
  [REQUEST_STATUS.APPROVED]: { tone: 'success', label: 'تایید شده', icon: '✅' },
  [REQUEST_STATUS.WITHDRAWN]: { tone: 'neutral', label: 'پس گرفته شده', icon: '↩️' },
};

// 🆕 کلید ذخیره‌سازی موارد حذف‌شده از تاریخچه (پایدار بین ناوبری‌ها)
const HIDDEN_HISTORY_KEY = 'teacher-hidden-request-history';



// 🔽 CHANGED: helper برای نمایش «پایه دهم (ریاضی)» — فقط اگه field داشته باشه
const formatGradeField = (grade, field) => {
  const g = GRADE_TITLES[String(grade)] || `پایه ${grade}`;
  if (!field) return g;
  const f = getFieldMeta(field);
  return f ? `${g} (${f.shortTitle})` : g;
};

// ================================================================
// SECTION 2: MAIN PAGE
// ================================================================

export default function TeacherBooksPage() {
  const { user } = useAuth();

  // Guard: فقط نقش teacher
  if (!user || user.role !== 'teacher') {
    return <TeacherAccessDenied user={user} />;
  }

  return (
    <BooksEngineProvider user={user}>
      <TeacherBooksInner />
    </BooksEngineProvider>
  );
}

// ================================================================
// SECTION 3: ACCESS DENIED
// ================================================================

function TeacherAccessDenied({ user }) {
  const navigate = useNavigate();
  const roleRedirects = {
    student: '/StudentHome',
    parent: '/ParentHome',
    admin: '/AdminHome',
    teacher: '/TeacherHome',
  };
  const target = roleRedirects[user?.role] || '/login';

  return (
    <div className="tb-denied">
      <div className="tb-denied-card">
        <div className="tb-denied-icon">🔐</div>
        <h2>دسترسی محدود</h2>
        <p>این صفحه مخصوص معلمان است.</p>
        <button className="tb-btn tb-btn-primary" onClick={() => navigate(target)}>
          بازگشت
        </button>
      </div>
    </div>
  );
}

// ================================================================
// SECTION 4: INNER PAGE
// ================================================================

function TeacherBooksInner() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useBooksEngine();
  const { ready, initError } = useEngineReady();

  // --- URL-based tab ---
  const initialTab = useMemo(() => {
    const p = new URLSearchParams(location.search).get('tab');
    return Object.values(TABS).includes(p) ? p : TABS.REQUESTS;
  }, [location.search]);
  const [activeTab, setActiveTab] = useState(initialTab);

  const handleTabChange = useCallback((tab) => {
    setActiveTab(tab);
    const params = new URLSearchParams(location.search);
    params.set('tab', tab);
    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
  }, [location.pathname, location.search, navigate]);

  // --- Data ---
  const teacherProfile = useTeacherByUserId(user?.id);
  const requests = useMyRequests();
  const projects = useMyProjects();
  const stats = useSystemStats();

  // --- Derived ---
  const pendingCount = useMemo(
    () => requests.filter((r) =>
      [REQUEST_STATUS.PENDING, REQUEST_STATUS.EDIT_REQUESTED].includes(r.status)
    ).length,
    [requests]
  );
  const draftCount = useMemo(
    () => projects.filter((p) => p.state === PROJECT_STATE.BUILDING).length,
    [projects]
  );
  const publishedCount = useMemo(
    () => projects.filter((p) => p.state === PROJECT_STATE.PUBLISHED).length,
    [projects]
  );

  // --- Request modal ---
  const [showRequestWizard, setShowRequestWizard] = useState(false);

    // 🌗 state تم تیره برای این صفحه
// 🌗 تم — وصل شده به ناوبار بالا (navbar2-drawer-dark)
// 🆕 خواندن از engine (به‌جای localStorage مستقیم) تا هماهنگ با remoteSync باشه
const [isDarkMode, setIsDarkMode] = useState(() => {
  try {
    return engine.getRaw('navbar2-drawer-dark') === '1';
  } catch {
    return false;
  }
});

const toggleTheme = useCallback(() => setIsDarkMode((prev) => !prev), []);

// ذخیره در کلید مشترک
// 🆕 استفاده از engine.setRaw تا event `storage:write` صادر بشه
//    و remoteSync بتونه آن را در userPrefs (دیتابیس) sync کنه
// 🆕 ست کردن data-theme روی html برای پشتیبانی از Portal (مودال اعلان‌ها)
useEffect(() => {
  if (typeof document === 'undefined') return;
  document.documentElement.setAttribute(
    'data-theme',
    isDarkMode ? 'dark' : 'light'
  );
}, [isDarkMode]);

// گوش دادن به تغییرات ناوبر
// گوش دادن به تغییرات ناوبار
useEffect(() => {
  const handleStorage = (e) => {
    if (e.key === 'navbar2-drawer-dark') {
      setIsDarkMode(e.newValue === '1');
    }
  };
  window.addEventListener('storage', handleStorage);

  const interval = setInterval(() => {
    try {
      // 🆕 خواندن از engine به‌جای localStorage مستقیم (هماهنگ با remoteSync)
      const current = engine.getRaw('navbar2-drawer-dark') === '1';
      setIsDarkMode((prev) => (prev !== current ? current : prev));
    } catch {}
  }, 500);

  return () => {
    window.removeEventListener('storage', handleStorage);
    clearInterval(interval);
  };
}, []);

  if (initError) {
    return (
      <div className="tb-error">
        <EmptyState
          icon="⚠️"
          title="خطا در راه‌اندازی"
          description={String(initError?.message || initError)}
        />
      </div>
    );
  }

  if (!ready) {
    return <TeacherLoading isDarkMode={isDarkMode} />;
  }

  return (
    <div className={`tb-page ${isDarkMode ? 'dark-mode' : ''}`}>
      <TeacherBackground />

      <header className="tb-header">
        <div className="tb-header-inner">
          <div className="tb-header-id">

            
            <div className="tb-avatar">
              {teacherProfile?.avatarBlobId ? (
                <BlobImage
                  blobId={teacherProfile.avatarBlobId}
                  fallbackSrc={teacherProfile.legacyAvatar}
                  alt={teacherProfile.fullName}
                />
              ) : teacherProfile?.legacyAvatar ? (
                <img src={teacherProfile.legacyAvatar} alt={teacherProfile.fullName} />
              ) : (
                <span className="tb-avatar-fallback">
                  {(teacherProfile?.fullName || user?.name || 'م').charAt(0)}
                </span>
              )}
            </div>
            <div className="tb-header-info">
              <h1 className="tb-header-name">
               سلام،  {teacherProfile?.fullName || user?.name || 'معلم عزیز'} 👋
              </h1>
              <div className="tb-header-meta">
                {teacherProfile?.folderName ? (
                  <Chip tone="purple">🏷️ {teacherProfile.folderName}</Chip>
                ) : (
                  <span className="tb-header-hint">هنوز معلم تأییدشده نیستید</span>
                )}
                {teacherProfile?.isActive === false && (
                  <Chip tone="danger">غیرفعال</Chip>
                )}
              </div>
            </div>

            {/* 🆕 دکمه بازگشت — استایل دایره‌ای و مناسب موبایل */}
            <button
              type="button"
              className="tb-theme-toggle"
              onClick={() => navigate(-1)}
              title="بازگشت"
              aria-label="بازگشت"
            > 
              <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M19 12H5M12 19l-7-7 7-7" />
              </svg>
            </button>
            
          </div>

          <div className="tb-header-actions">
            <NotificationBell onNavigate={(link) => navigate(link)} />
            
            
          </div>
        </div>

        {/* 🔽 بنر جدید — درس‌های دانش‌آموزان */}
{/* 🔽 بنر درس‌های دانش‌آموزان */}
<button
  
  className="tb-students-banner"
  /*onClick={() => navigate('/student-lessons')}*/
  aria-label="مشاهده درس‌های دانش‌آموزان"
>
  <div className="tb-students-banner-bg">
    <img
      src="/images/book-teacher-1.jpg"
      alt=""
      loading="lazy"
      onError={(e) => { e.currentTarget.style.display = 'none'; }}
    />
  </div>
  {/*<div className="tb-students-banner-overlay" aria-hidden="true" />
  <div className="tb-students-banner-content">
    <div className="tb-students-banner-info">
      
      <div>
        
        <p className="tb-students-banner-desc">مشاهده‌ی محتوای آموزشی همه‌ی معلم‌ها</p>
      </div>
    </div>
    <span className="tb-students-banner-view">
      <span className="tb-students-banner-view-icon">👁️</span>
      <span>مشاهده آموزش های دانش‌آموزان و معلمان</span>
    </span>
  </div>*/}
</button>
{/* 🔼 پایان بنر */}

        <div className="tb-quick-stats">
          <QuickStat
            icon="⏳"
            value={pendingCount}
            label="درخواست در انتظار"
            tone="warning"
            onClick={() => handleTabChange(TABS.REQUESTS)}
          />
          <QuickStat
            icon="✏️"
            value={draftCount}
            label="در حال ساخت"
            tone="info"
            onClick={() => handleTabChange(TABS.PROJECTS)}
          />
          <QuickStat
            icon="✅"
            value={publishedCount}
            label="منتشرشده"
            tone="success"
            onClick={() => handleTabChange(TABS.PROJECTS)}
          />
          <QuickStat
            icon="📚"
            value={teacherProfile ? '—' : '—'}
            label="کتاب‌های من"
            tone="neutral"
            onClick={() => handleTabChange(TABS.PROJECTS)}
            hidden={!teacherProfile}
          />
        </div>
      </header>

      <nav className="tb-tabs" role="tablist">
        {Object.entries(TAB_LABELS).map(([key, label]) => {
          const badge = key === TABS.REQUESTS ? pendingCount
            : key === TABS.PROJECTS ? projects.length
            : 0;
          return (
            <button
              key={key}
              role="tab"
              aria-selected={activeTab === key}
              className={`tb-tab ${activeTab === key ? 'active' : ''}`}
              onClick={() => handleTabChange(key)}
            >
              <span className="tb-tab-label">{label}</span>
              {badge > 0 && <span className="tb-tab-badge">{badge}</span>}
            </button>
          );
        })}
      </nav>

      <main className="tb-main">
        {activeTab === TABS.REQUESTS && (
          <RequestsTab
            requests={requests}
            teacherProfile={teacherProfile}
            onNewRequest={() => setShowRequestWizard(true)}
            onRefresh={() => {}}
          />
        )}
        {activeTab === TABS.PROJECTS && (
          <ProjectsTab
            projects={projects}
            teacherProfile={teacherProfile}
          />
        )}
        {activeTab === TABS.PROFILE && (
          <ProfileTab
            teacherProfile={teacherProfile}
          />
        )}
      </main>

      {showRequestWizard && (
  <TeacherRequestWizard
    onClose={() => setShowRequestWizard(false)}
    onSuccess={() => {
      setShowRequestWizard(false);
      handleTabChange(TABS.REQUESTS);
    }}
    isDarkMode={isDarkMode}   
  />
)}
    </div>
  );
}

// ================================================================
// SECTION 5: BACKGROUND + LOADING
// ================================================================

function TeacherBackground() {
  return (
    <div className="tb-bg" aria-hidden="true">
      <div className="tb-bg-orb tb-bg-orb-1" />
      <div className="tb-bg-orb tb-bg-orb-2" />
      <div className="tb-bg-orb tb-bg-orb-3" />
      <div className="tb-bg-grid" />
    </div>
  );
}

function TeacherLoading({ isDarkMode }) {
  return (
    <div className={`tb-page ${isDarkMode ? 'dark-mode' : ''}`}>
      <TeacherBackground />
      <header className="tb-header">
        <div className="tb-header-inner">
          <SkeletonLoader variant="avatar" />
          <SkeletonLoader variant="line" />
        </div>
      </header>
      <div className="tb-main">
        <SkeletonLoader variant="card" count={3} />
      </div>
    </div>
  );
}

// ================================================================
// SECTION 6: QUICK STAT
// ================================================================

function QuickStat({ icon, value, label, tone = 'neutral', onClick, hidden }) {
  if (hidden) return null;
  return (
    <button
      type="button"
      className={`tb-stat tb-stat-${tone}`}
      onClick={onClick}
    >
      <span className="tb-stat-icon">{icon}</span>
      <div className="tb-stat-body">
        <span className="tb-stat-value">{value}</span>
        <span className="tb-stat-label">{label}</span>
      </div>
    </button>
  );
}

// ================================================================
// SECTION 7: REQUESTS TAB
// ================================================================

function RequestsTab({ requests, teacherProfile, onNewRequest }) {
  const navigate = useNavigate();

  const [confirmWithdraw, setConfirmWithdraw] = useState(null);
  // 🆕 state برای حذف از تاریخچه — با بازیابی از engine
  const [hiddenHistoryIds, setHiddenHistoryIds] = useState(() => {
    try {
      const raw = engine.getRaw(HIDDEN_HISTORY_KEY);
      if (!raw) return new Set();
      const arr = JSON.parse(raw);
      return new Set(Array.isArray(arr) ? arr : []);
    } catch {
      return new Set();
    }
  });
  const [confirmHideHistory, setConfirmHideHistory] = useState(null);
  const [confirmHideAllHistory, setConfirmHideAllHistory] = useState(false); // 🆕
  const { withdraw } = useTeacherRequestActions();

  const active = requests.filter((r) =>
    r.status !== REQUEST_STATUS.WITHDRAWN &&
    r.status !== REQUEST_STATUS.APPROVED &&
    r.status !== REQUEST_STATUS.REJECTED
  );
  const history = requests.filter(
    (r) =>
      (r.status === REQUEST_STATUS.WITHDRAWN ||
        r.status === REQUEST_STATUS.APPROVED ||
        r.status === REQUEST_STATUS.REJECTED) &&
      !hiddenHistoryIds.has(r.id) // 🆕 موارد حذف‌شده نمایش داده نشوند
  );

  const handleWithdraw = useCallback(async () => {
    if (!confirmWithdraw) return;
    await withdraw.run(confirmWithdraw.id);
    setConfirmWithdraw(null);
  }, [confirmWithdraw, withdraw]);

  // 🆕 حذف از تاریخچه (فقط مخفی‌سازی در UI)
  const handleHideHistory = useCallback(() => {
    if (!confirmHideHistory) return;
    const id = confirmHideHistory.id;
    setHiddenHistoryIds((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
    setConfirmHideHistory(null);
  }, [confirmHideHistory]);

  // 🆕 حذف همه‌ی تاریخچه
  const handleHideAllHistory = useCallback(() => {
    setHiddenHistoryIds((prev) => {
      const next = new Set(prev);
      requests.forEach((r) => {
        if (
          r.status === REQUEST_STATUS.WITHDRAWN ||
          r.status === REQUEST_STATUS.APPROVED ||
          r.status === REQUEST_STATUS.REJECTED
        ) {
          next.add(r.id);
        }
      });
      return next;
    });
    setConfirmHideAllHistory(false);
  }, [requests]);

  // 🆕 ذخیره‌سازی پایدار در engine (تا با ناوبری صفحه از بین نره)
  useEffect(() => {
    try {
      engine.setRaw(HIDDEN_HISTORY_KEY, JSON.stringify(Array.from(hiddenHistoryIds)));
    } catch {}
  }, [hiddenHistoryIds]);

  if (requests.length === 0 && !teacherProfile) {
    return (
      <div className="tb-tab-body">
        <div className="tb-hero-card">
          <div className="tb-hero-icon">🎓</div>
          <h2>به عنوان معلم به ما بپیوندید</h2>
          <p>
            با ثبت درخواست، می‌توانید برای یکی از کتاب‌های درسی در یکی از پایه‌ها معلم شوید
            و محتوای آموزشی خود را با دانش‌آموزان به اشتراک بگذارید.
          </p>
          <button className="tb-btn tb-btn-primary tb-btn-lg" onClick={onNewRequest}>
            ➕ ثبت درخواست معلمی
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="tb-tab-body">
      <div className="tb-tab-toolbar">
        <h2 className="tb-section-title" style={{ margin: 0 }}></h2>
        <button className="tb-btn tb-btn-primary" onClick={onNewRequest}>
          ➕ درخواست جدید
        </button>
      </div>

      {active.length > 0 && (
        <section className="tb-section">
          <h3 className="tb-section-subtitle">فعال</h3>
          <div className="tb-request-list">
            {active.map((r) => (
              <RequestCard
                key={r.id}
                request={r}
                onWithdraw={() => setConfirmWithdraw(r)}
                onEdit={() => onNewRequest(r)}
              />
            ))}
          </div>
        </section>
      )}

      {history.length > 0 && (
        <section className="tb-section">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <h3 className="tb-section-subtitle" style={{ margin: 0 }}>تاریخچه</h3>
            <button
              type="button"
              className="tb-btn tb-btn-ghost"
              onClick={() => setConfirmHideAllHistory(true)}
              style={{ color: '#dc2626' }}
              title="حذف همه‌ی تاریخچه"
            >
              🗑️ حذف همه
            </button>
          </div>
          <div className="tb-request-list">
            {history.map((r) => (
              <RequestCard
                key={r.id}
                request={r}
                compact
                onHideFromHistory={() => setConfirmHideHistory(r)} // 🆕
              />
            ))}
          </div>
        </section>
      )}

      {requests.length === 0 && teacherProfile && (
        <EmptyState
          icon="📭"
          title="هنوز درخواستی ثبت نکرده‌اید"
          description="برای شروع، یک درخواست جدید ثبت کنید."
          action={
            <button className="tb-btn tb-btn-primary" onClick={onNewRequest}>
              ثبت درخواست
            </button>
          }
        />
      )}

      <ConfirmModal
        isOpen={!!confirmWithdraw}
        onClose={() => setConfirmWithdraw(null)}
        onConfirm={handleWithdraw}
        title="پس گرفتن درخواست"
        message="آیا می‌خواهید این درخواست را پس بگیرید؟ پس از این کار می‌توانید درخواست جدید ثبت کنید."
        confirmText="پس بگیر"
        tone="warning"
        loading={withdraw.loading}
      />

      {/* 🆕 Modal تأیید حذف از تاریخچه */}
      <ConfirmModal
        isOpen={!!confirmHideHistory}
        onClose={() => setConfirmHideHistory(null)}
        onConfirm={handleHideHistory}
        title="حذف از تاریخچه"
        message="آیا از حذف این مورد از تاریخچه مطمئن هستید؟"
        confirmText="🗑️ حذف"
        cancelText="انصراف"
        tone="danger"
      />

      {/* 🆕 Modal تأیید حذف همه‌ی تاریخچه */}
      <ConfirmModal
        isOpen={confirmHideAllHistory}
        onClose={() => setConfirmHideAllHistory(false)}
        onConfirm={handleHideAllHistory}
        title="حذف همه‌ی تاریخچه"
        message={`آیا از حذف تمام ${history.length} مورد تاریخچه مطمئن هستید؟ این عملیات قابل بازگشت نیست.`}
        confirmText="🗑️ حذف همه"
        cancelText="انصراف"
        tone="danger"
      />
    </div>
  );
}

function RequestCard({ request, onWithdraw, onEdit, compact, onHideFromHistory }) {
  const statusUI = REQUEST_STATUS_UI[request.status] || REQUEST_STATUS_UI.draft;
  // 🔽 CHANGED: نمایش کامل با رشته
  const gradeLabel = formatGradeField(request.grade, request.field);
  const subjectLabel = SUBJECT_LABELS[request.subject] || request.subject;

  return (
    <article className={`tb-request-card ${compact ? 'compact' : ''}`}>
      <div className="tb-request-header">
        <div className="tb-request-title">
          <span className="tb-request-emoji">📖</span>
          <div>
            <h4>{subjectLabel} — {gradeLabel}</h4>
            <span className="tb-request-folder">
              🏷️ {request.proposedFolderName}
            </span>
          </div>
        </div>
        <Badge tone={statusUI.tone}>
          {statusUI.icon} {statusUI.label}
        </Badge>
      </div>

      {request.adminNotes && (
        <div 
          className={`tb-request-note tb-request-note-${statusUI.tone}`} 
          style={{
            border: '2px dashed',
            borderColor: statusUI.tone === 'danger' ? '#fca5a5' : '#93c5fd',
            background: statusUI.tone === 'danger' ? '#fee2e2' : '#eff6ff',
            padding: '12px 16px',
            borderRadius: 10,
            marginTop: 10
          }}
        >
          <strong style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4, fontSize: '0.9rem' }}>
            <span>📢</span> پیام از طرف مدیر:
          </strong>
          <div style={{ fontSize: '0.95rem', fontWeight: 700, lineHeight: 1.6 }}>
            {request.adminNotes}
          </div>
        </div>
      )}

      <div className="tb-request-meta">
        <span>
          📅 {new Date(request.createdAt).toLocaleDateString('fa-IR')}
        </span>
        {request.submittedAt && (
          <span>
            ✉️ ارسال: {new Date(request.submittedAt).toLocaleDateString('fa-IR')}
          </span>
        )}
      </div>

      {!compact && (
        <div className="tb-request-actions">
          {request.status === REQUEST_STATUS.EDIT_REQUESTED && onEdit && (
            <button className="tb-btn tb-btn-primary" onClick={onEdit}>
              ✏️ ویرایش و ارسال مجدد
            </button>
          )}
          {[REQUEST_STATUS.DRAFT, REQUEST_STATUS.PENDING, REQUEST_STATUS.EDIT_REQUESTED].includes(request.status) && onWithdraw && (
            <button className="tb-btn tb-btn-ghost" onClick={onWithdraw}>
              ↩️ پس بگیر
            </button>
          )}
        </div>
      )}

      {/* 🆕 دکمه‌ی حذف — فقط در حالت تاریخچه (compact) */}
      {compact && onHideFromHistory && (
        <div className="tb-request-actions">
          <button
            type="button"
            className="tb-btn tb-btn-ghost"
            onClick={onHideFromHistory}
            style={{ color: '#dc2626' }}
          >
            🗑️ حذف از تاریخچه
          </button>
        </div>
      )}
    </article>
  );
}

// ================================================================
// SECTION 8: PROJECTS TAB
// ================================================================

function ProjectsTab({ projects, teacherProfile }) {
  const navigate = useNavigate();
  const [filter, setFilter] = useState('all');

  const filtered = useMemo(() => {
    if (filter === 'all') return projects;
    return projects.filter((p) => p.state === filter);
  }, [projects, filter]);

  if (!teacherProfile) {
    return (
      <div className="tb-tab-body">
        <EmptyState
          icon="🔒"
          title="ابتدا باید معلم تاییدشده باشید"
          description="برای ساخت پروژه، ابتدا درخواست معلمی ثبت کنید و منتظر تایید مدیر باشید."
        />
      </div>
    );
  }

  return (
    <div className="tb-tab-body">
      <div className="tb-tab-toolbar">
        <h2 className="tb-section-title">پروژه‌های من</h2>
        <div className="tb-filters">
          <FilterChip active={filter === 'all'} onClick={() => setFilter('all')}>همه</FilterChip>
          <FilterChip
            active={filter === PROJECT_STATE.BUILDING}
            onClick={() => setFilter(PROJECT_STATE.BUILDING)}
          >
            پیش‌نویس
          </FilterChip>
          <FilterChip
            active={filter === PROJECT_STATE.PUBLISHED}
            onClick={() => setFilter(PROJECT_STATE.PUBLISHED)}
          >
            منتشرشده
          </FilterChip>
          <FilterChip
            active={filter === PROJECT_STATE.UPDATE_PENDING}
            onClick={() => setFilter(PROJECT_STATE.UPDATE_PENDING)}
          >
            در انتظار بروزرسانی
          </FilterChip>
        </div>
      </div>

      {filtered.length === 0 ? (
        <EmptyState
          icon="📦"
          title="پروژه‌ای یافت نشد"
          description={
            projects.length === 0
              ? 'هنوز پروژه‌ای نساخته‌اید. درخواست معلمی ثبت کنید تا پروژه‌ی شما ساخته شود.'
              : 'هیچ پروژه‌ای با این فیلتر وجود ندارد.'
          }
        />
      ) : (
        <div className="tb-projects-grid">
          {filtered.map((p) => (
            <ProjectCard key={p.id} project={p} />
          ))}
        </div>
      )}
    </div>
  );
}

function FilterChip({ active, onClick, children }) {
  return (
    <button
      className={`tb-filter-chip ${active ? 'active' : ''}`}
      onClick={onClick}
      type="button"
    >
      {children}
    </button>
  );
}

function ProjectCard({ project }) {
  const navigate = useNavigate();
  const { toast } = useBooksEngine();
  const { hardDelete } = useProjectActions();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // 🔽 CHANGED: نمایش کامل با رشته
  const gradeLabel = formatGradeField(project.grade, project.field);
  const subjectLabel = SUBJECT_LABELS[project.subject] || project.subject;

  const canEdit =
    project.state === PROJECT_STATE.BUILDING ||
    project.state === PROJECT_STATE.PUBLISHED;

  // ✅ فقط پروژه‌های غیر در انتظار بررسی قابل حذف هستند
  const canDelete =
    project.state === PROJECT_STATE.BUILDING ||
    project.state === PROJECT_STATE.PUBLISHED ||
    project.state === PROJECT_STATE.SUSPENDED;

  const handleOpen = () => {
    navigate(`/teacher/books/edit/${project.id}`);
  };

  const handleDelete = async () => {
    const res = await hardDelete.run(project.id);
    if (res?.ok) {
      setShowDeleteConfirm(false);
      toast?.('پروژه و تمام محتوای آن حذف شد', 'success');
    }
  };

  // آیا پروژه منتشرشده است؟ → هشدار جدی‌تر
  const isPublished = !!project.publishedVersionId;

  return (
    <>
      <article className="tb-project-card">
        <div className="tb-project-cover">
          {project.coverImageBlobId ? (
            <BlobImage
              blobId={project.coverImageBlobId}
              alt={project.title}
              className="tb-project-cover-img"
            />
          ) : (
            <div className={`tb-project-cover-placeholder tb-project-cover-g${project.grade}`}>
              <span className="tb-project-cover-emoji">📘</span>
            </div>
          )}

          <div className="tb-project-badges">
            <ProjectStateBadge state={project.state} size="sm" />
          </div>

          {/* ✅ دکمه‌ی حذف — گوشه‌ی مخالف badges */}
          {canDelete && (
            <button
              type="button"
              className="tb-project-delete-btn"
              onClick={(e) => {
                e.stopPropagation();
                setShowDeleteConfirm(true);
              }}
              title="حذف کامل پروژه"
              aria-label="حذف پروژه"
            >
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none">
                <path
                  d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6M10 11v6M14 11v6"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          )}
        </div>

        <div className="tb-project-body">
          <h3 className="tb-project-title">
            {project.title || `${subjectLabel} — ${gradeLabel}`}
          </h3>
          <div className="tb-project-subtitle">
            {gradeLabel} · {subjectLabel}
          </div>

          <div className="tb-project-stats">
            <StatPill icon="📚" value={project.versions?.length || 0} label="نسخه" />
            <StatPill icon="👥" value={project.stats?.studentsCount || 0} label="دانش‌آموز" />
            {project.stats?.avgRating > 0 && (
              <StatPill icon="⭐" value={project.stats.avgRating.toFixed(1)} label="" />
            )}
          </div>

          {project.publishedVersionId && (
            <div className="tb-project-published">
              <span className="tb-dot tb-dot-success" />
              نسخه منتشرشده فعال است
            </div>
          )}
          {project.pendingVersionId && (
            <div className="tb-project-pending">
              <span className="tb-dot tb-dot-warning" />
              نسخه جدید در انتظار بررسی مدیر
            </div>
          )}
        </div>

        <div className="tb-project-actions">
          <button
            className="tb-btn tb-btn-primary tb-btn-block"
            onClick={handleOpen}
            disabled={!canEdit}
          >
            {canEdit ? '✏️ ادامه ویرایش' : '🔒 قفل شده'}
          </button>
        </div>
      </article>

      {/* ✅ Modal تأیید حذف */}
      <ConfirmModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDelete}
        title="حذف کامل پروژه"
        message={
          isPublished
            ? `⚠️ هشدار: پروژه "${project.title}" منتشرشده است و ${project.stats?.studentsCount || 0} دانش‌آموز دارد. حذف این پروژه باعث می‌شود تمام فصل‌ها، درس‌ها، مراحل و ویدیوها پاک شوند و این عمل قابل بازگشت نیست.`
            : `آیا از حذف کامل پروژه "${project.title}" مطمئن هستید؟ تمام فصل‌ها، درس‌ها، مراحل و ویدیوهای این پروژه برای همیشه حذف می‌شوند.`
        }
        confirmText="🗑️ حذف کامل"
        cancelText="انصراف"
        tone="danger"
        loading={hardDelete.loading}
      />
    </>
  );
}

function StatPill({ icon, value, label }) {
  return (
    <span className="tb-stat-pill">
      <span className="tb-stat-pill-icon">{icon}</span>
      <span className="tb-stat-pill-value">{value}</span>
      {label && <span className="tb-stat-pill-label">{label}</span>}
    </span>
  );
}

// ================================================================
// SECTION 9: PROFILE TAB
// ================================================================

function ProfileTab({ teacherProfile }) {
  const { user, toast } = useBooksEngine();
  const { upload: uploadImage, uploading } = useImageUpload();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(() => ({
    fullName: teacherProfile?.fullName || user?.name || '',
    bio: teacherProfile?.bio || '',
    experienceYears: teacherProfile?.experienceYears ?? 0,
    specialties: teacherProfile?.specialties || [],
    avatarBlobId: teacherProfile?.avatarBlobId || null,
    socialLinks: teacherProfile?.socialLinks || {},
  }));
  const [saving, setSaving] = useState(false);
  const [specialtyInput, setSpecialtyInput] = useState('');

  useEffect(() => {
    if (teacherProfile) {
      setForm({
        fullName: teacherProfile.fullName || '',
        bio: teacherProfile.bio || '',
        experienceYears: teacherProfile.experienceYears ?? 0,
        specialties: teacherProfile.specialties || [],
        avatarBlobId: teacherProfile.avatarBlobId || null,
        socialLinks: teacherProfile.socialLinks || {},
      });
    }
  }, [teacherProfile]);

  const handleSave = useCallback(async () => {
    if (!teacherProfile) return;
    setSaving(true);
    try {
      const res = dataEngine.teacher.update(teacherProfile.id, form);
      if (res.ok) {
        toast?.('پروفایل ذخیره شد', 'success');
        setEditing(false);
      } else {
        const msg = (res.errors || ['خطای نامشخص'])[0];
        toast?.(humanizeFormError(msg), 'error');
      }
    } catch (e) {
      toast?.('خطا در ذخیره', 'error');
    } finally {
      setSaving(false);
    }
  }, [teacherProfile, form, toast]);

  const handleAddSpecialty = useCallback(() => {
    const v = specialtyInput.trim();
    if (!v) return;
    if (form.specialties.includes(v)) return;
    setForm((f) => ({ ...f, specialties: [...f.specialties, v] }));
    setSpecialtyInput('');
  }, [specialtyInput, form.specialties]);

  const handleRemoveSpecialty = useCallback((s) => {
    setForm((f) => ({ ...f, specialties: f.specialties.filter((x) => x !== s) }));
  }, []);

  const handleAvatarChange = useCallback((blobId) => {
    setForm((f) => ({ ...f, avatarBlobId: blobId }));
  }, []);

  useKeyboardShortcuts({
    'ctrl+s': () => { if (editing) handleSave(); },
    'mod+s': () => { if (editing) handleSave(); },
  }, { enabled: editing });

  if (!teacherProfile) {
    return (
      <div className="tb-tab-body">
        <EmptyState
          icon="👤"
          title="هنوز معلم تاییدشده نیستید"
          description="پس از تایید درخواست، پروفایل شما ساخته می‌شود."
        />
      </div>
    );
  }

  return (
    <div className="tb-tab-body">
      <div className="tb-tab-toolbar">
        <h2 className="tb-section-title">پروفایل معلم</h2>
        {!editing ? (
          <button className="tb-btn tb-btn-primary" onClick={() => setEditing(true)}>
            ✏️ ویرایش
          </button>
        ) : (
          <div className="tb-btn-group">
            <button
              className="tb-btn tb-btn-ghost"
              onClick={() => setEditing(false)}
              disabled={saving}
            >
              انصراف
            </button>
            <button
              className="tb-btn tb-btn-primary"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? <Spinner size={14} /> : '💾'} ذخیره
            </button>
          </div>
        )}
      </div>

      <div className="tb-profile-card">
        <div className="tb-profile-avatar-col">
          {editing ? (
            <ImageUploader
              value={form.avatarBlobId}
              onChange={handleAvatarChange}
              label=""
              aspect="1/1"
              refType="avatar"
              hint="حداکثر ۲ مگابایت"
            />
          ) : (
            <div className="tb-profile-avatar-display">
              {teacherProfile.avatarBlobId ? (
                <BlobImage blobId={teacherProfile.avatarBlobId} alt={teacherProfile.fullName} />
              ) : teacherProfile.legacyAvatar ? (
                <img src={teacherProfile.legacyAvatar} alt={teacherProfile.fullName} />
              ) : (
                <span className="tb-avatar-fallback">
                  {(teacherProfile.fullName || 'م').charAt(0)}
                </span>
              )}
            </div>
          )}
        </div>

        <div className="tb-profile-info-col">
          <div className="tb-field">
            <label className="tb-label">نام و نام خانوادگی</label>
            {editing ? (
              <input
                className="tb-input"
                value={form.fullName}
                onChange={(e) => setForm((f) => ({ ...f, fullName: e.target.value }))}
                maxLength={100}
              />
            ) : (
              <div className="tb-readonly">{teacherProfile.fullName}</div>
            )}
          </div>

          <div className="tb-field">
            <label className="tb-label">شناسه پوشه (غیرقابل تغییر)</label>
            <div className="tb-readonly tb-readonly-code">
              <code>{teacherProfile.folderName}</code>
              <span className="tb-lock-icon">🔒</span>
            </div>
            <div className="tb-hint">
              این شناسه پس از اولین انتشار قابل تغییر نیست.
            </div>
          </div>

          <div className="tb-field">
            <label className="tb-label">سابقه تدریس (سال)</label>
            {editing ? (
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                className="tb-input"
                value={form.experienceYears || ''}
                onFocus={(e) => e.target.select()}
                onChange={(e) => {
                  const val = e.target.value
                    .replace(/[۰-۹]/g, d => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(d)))
                    .replace(/[٠-٩]/g, d => String('٠١٢٣٤٥٦٧٨٩'.indexOf(d)))
                    .replace(/[^0-9]/g, '');
                  setForm((f) => ({
                    ...f,
                    experienceYears: val ? Math.min(80, Number(val)) : 0,
                  }));
                }}
              />
            ) : (
              <div className="tb-readonly">{teacherProfile.experienceYears || 0} سال</div>
            )}
          </div>

          <div className="tb-field">
            <label className="tb-label">بیوگرافی</label>
            {editing ? (
              <textarea
                className="tb-textarea"
                rows={4}
                maxLength={2000}
                value={form.bio}
                onChange={(e) => setForm((f) => ({ ...f, bio: e.target.value }))}
                placeholder="درباره خودتان و روش تدریس‌تان..."
              />
            ) : (
              <div className="tb-readonly tb-readonly-long">
                {teacherProfile.bio || <span className="tb-muted">بدون توضیح</span>}
              </div>
            )}
            {editing && (
              <div className="tb-hint">{form.bio.length} / 2000 کاراکتر</div>
            )}
          </div>

          <div className="tb-field">
            <label className="tb-label">تخصص‌ها</label>
            {editing && (
              <div className="tb-inline-add">
                <input
                  className="tb-input"
                  value={specialtyInput}
                  onChange={(e) => setSpecialtyInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddSpecialty(); } }}
                  placeholder="مثلاً: آموزش بازی‌محور"
                  maxLength={50}
                />
                <button
                  type="button"
                  className="tb-btn tb-btn-ghost"
                  onClick={handleAddSpecialty}
                  disabled={!specialtyInput.trim()}
                >
                  افزودن
                </button>
              </div>
            )}
            <div className="tb-chips">
              {form.specialties.length === 0 ? (
                <span className="tb-muted">تخصصی ثبت نشده</span>
              ) : (
                form.specialties.map((s) => (
                  <Chip
                    key={s}
                    tone="purple"
                    onRemove={editing ? () => handleRemoveSpecialty(s) : null}
                  >
                    {s}
                  </Chip>
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {!editing && (
        <div className="tb-profile-meta">
          <span>📅 عضویت از {new Date(teacherProfile.createdAt).toLocaleDateString('fa-IR')}</span>
          {teacherProfile.isLegacy && <Badge tone="info">معلم قدیمی</Badge>}
          {teacherProfile.isActive ? (
            <Badge tone="success">✓ فعال</Badge>
          ) : (
            <Badge tone="danger">غیرفعال</Badge>
          )}
        </div>
      )}
    </div>
  );
}

function humanizeFormError(code) {
  const map = {
    folder_name_locked: 'شناسه پوشه پس از انتشار قابل تغییر نیست.',
    folder_name_taken: 'این شناسه پوشه قبلاً استفاده شده.',
    full_name_required: 'نام و نام خانوادگی الزامی است.',
    full_name_too_long: 'نام بیش از حد طولانی است.',
    bio_too_long: 'بیوگرافی بیش از ۲۰۰۰ کاراکتر است.',
    experience_years_out_of_range: 'سابقه تدریس باید بین ۰ تا ۸۰ باشد.',
    not_found: 'پروفایل یافت نشد.',
  };
  return map[code] || code || 'خطای نامشخص';
}

// ================================================================
// SECTION 10: TEACHER REQUEST WIZARD
// ================================================================

const WIZARD_STEPS = [
  { key: 'basics',   label: 'انتخاب کتاب' },
  { key: 'profile',  label: 'اطلاعات حرفه‌ای' },
  { key: 'confirm',  label: 'بازبینی و ثبت' },
];

function TeacherRequestWizard({ onClose, onSuccess, isDarkMode }) {
  const { user, toast } = useBooksEngine();
  const { submit } = useTeacherRequestActions();

  const [step, setStep] = useState(0);

   // form state
  const [grade, setGrade] = useState('');
  const [field, setField] = useState('');   // 🔽 CHANGED
  const [subject, setSubject] = useState('');
  const [folderName, setFolderName] = useState('');
  const [formData, setFormData] = useState({
    fullName: user?.name || '',
    bio: '',
    experienceYears: 0,
    specialties: [],
    avatarBlobId: null,
    certificateBlobIds: [],
    socialLinks: {},
  });

  const [specialtyInput, setSpecialtyInput] = useState('');
  const [idempotencyKey] = useState(() => `${user?.id || 'anon'}-${Date.now()}`);

  const [submitError, setSubmitError] = useState(null);   // ✅ جدید
const requestTick = useEngineTick(['registry:request:changed', 'registry:project:changed']);   // ✅ جدید

  // auto-generate folderName وقتی نام تغییر می‌کنه
  useEffect(() => {
    if (!folderName && formData.fullName) {
      const gen = dataEngine.utils.generateFolderName(formData.fullName);
      setFolderName(gen);
    }
  }, [formData.fullName, folderName]);

  // 🔽 CHANGED: لیست کتاب‌ها بر اساس پایه + رشته
  const subjectsForGrade = useMemo(() => {
    if (!grade) return [];
    const needsF = curriculumNeedsField(grade);
    if (needsF) {
      const f = field ? (curriculumNormalizeField(field) || null) : null;
      if (!f) return [];
      return getSubjectIdsFor(grade, f);
    }
    return getSubjectIdsFor(grade);
  }, [grade, field]);

  // 🔽 CHANGED: در صورت تغییر پایه/رشته، درس ناسازگار رو پاک کن
  useEffect(() => {
    if (subject && !subjectsForGrade.includes(subject)) {
      setSubject('');
    }
  }, [subject, subjectsForGrade]);

  // canRequest بررسی
  const requestability = useMemo(() => {
    if (!grade || !subject) return { ok: false };
    // 🔽 CHANGED: پاس‌دادن field
    return dataEngine.query.canRequestSubject(
      user?.id,
      grade,
      subject,
      field || null
    );
  }, [grade, subject, field, user?.id, requestTick]);

  const validateStep1 = useCallback(() => {
  setSubmitError(null);
  if (!grade) { setSubmitError('پایه تحصیلی را انتخاب کنید'); return false; }
  // 🔽 CHANGED: چک رشته برای ۱۰-۱۲
  if (curriculumNeedsField(grade) && !field) {
    setSubmitError('رشته تحصیلی را انتخاب کنید');
    return false;
  }
  if (!subject) { setSubmitError('کتاب درسی را انتخاب کنید'); return false; }
  if (!requestability.ok) {
    const err = requestability.error;
    const msg = {
      pending_request_exists: 'شما درخواست در انتظار برای این کتاب دارید.',
      already_teacher_for_subject: 'شما قبلاً معلم این کتاب هستید.',
      invalid_grade_or_subject: 'انتخاب شما معتبر نیست.',
    }[err] || err;
    setSubmitError(msg);
    return false;
  }
  return true;
}, [grade, subject, requestability]);

const validateStep2 = useCallback(() => {
  setSubmitError(null);
  if (!formData.fullName.trim()) { setSubmitError('نام و نام خانوادگی را وارد کنید'); return false; }
  if (formData.fullName.length > 100) { setSubmitError('نام بیش از حد طولانی است'); return false; }
  if (formData.bio.length > 2000) { setSubmitError('بیوگرافی بیش از ۲۰۰۰ کاراکتر است'); return false; }
  if (!folderName) { setSubmitError('شناسه پوشه الزامی است'); return false; }
  const v = dataEngine.validators.validateFolderName(folderName);
  if (!v.ok) { setSubmitError('شناسه پوشه معتبر نیست (فقط حرف، عدد و خط تیره)'); return false; }
  if (dataEngine.teacher.getByFolderName(folderName)) {
    setSubmitError('این شناسه پوشه قبلاً استفاده شده');
    return false;
  }
  return true;
}, [formData, folderName]);

  const handleNext = useCallback(() => {
    if (step === 0 && !validateStep1()) return false;
    if (step === 1 && !validateStep2()) return false;
    return true;
  }, [step, validateStep1, validateStep2]);

  const handleSubmit = useCallback(async () => {
  setSubmitError(null);
  if (!validateStep1()) return;
  if (!validateStep2()) return;

  const res = await submit.run({
    grade,
    field: field || null,   // 🔽 CHANGED
    subject,
    formData,
    proposedFolderName: folderName,
    idempotencyKey,
  });

  if (res?.ok) {
    toast?.('درخواست شما با موفقیت ثبت شد ✨', 'success');
    onSuccess?.();
  } else {
    const err = (res?.errors || [])[0] || 'submit_failed';
    const msg = {
      duplicate_pending_request: 'درخواست تکراری — قبلاً برای این کتاب درخواست داده‌اید',
      rate_limited: 'تعداد درخواست‌ها زیاد است. کمی صبر کنید.',
      folder_name_taken: 'این شناسه پوشه قبلاً استفاده شده',
      folder_name_invalid_format: 'فرمت شناسه پوشه معتبر نیست',
      folder_name_reserved: 'این شناسه پوشه رزرو شده است',
      grade_invalid: 'پایه تحصیلی معتبر نیست',
      subject_invalid: 'کتاب درسی معتبر نیست',
      pending_request_exists: 'شما در حال حاضر درخواست در انتظار برای این کتاب دارید',
      already_teacher_for_subject: 'شما قبلاً معلم این کتاب هستید',
      forbidden: 'دسترسی شما به این عملیات مجاز نیست',
      invalid_grade_or_subject: 'انتخاب شما معتبر نیست',
    }[err] || err;
    setSubmitError(msg);
    toast?.(msg, 'error');
  }
}, [grade, field, subject, formData, folderName, idempotencyKey, submit, toast, onSuccess, validateStep1, validateStep2]);


  const regenerateFolder = useCallback(() => {
    const gen = dataEngine.utils.generateFolderName(formData.fullName || 'Teacher');
    setFolderName(gen);
  }, [formData.fullName]);

  return (
    <Modal
      isOpen
      onClose={onClose}
      title="ثبت درخواست معلمی"
      size="lg"
      className={`tb-page ${isDarkMode ? 'dark-mode' : ''}`}
      closeOnBackdrop={!submit.loading}
      closeOnEsc={!submit.loading}
    >
      <Wizard
  steps={WIZARD_STEPS}
  currentStep={step}
  onStepChange={(s) => { setSubmitError(null); setStep(s); }}
  onFinish={handleSubmit}
  onCancel={onClose}
  loading={submit.loading}
  error={submitError}
  finishText="📤 ثبت درخواست"
>
        {(key) => (
          <>
            {key === 'basics' && (
              <StepBasics
                grade={grade}
                field={field}
                subject={subject}
                subjectsForGrade={subjectsForGrade}
                onGradeChange={(g) => {
                  setGrade(g);
                  // 🔽 CHANGED: ریست رشته اگه پایه جدید نیازی نداره
                  if (!curriculumNeedsField(g)) setField('');
                  setSubject('');
                }}
                onFieldChange={(f) => {
                  setField(f);
                  setSubject('');
                }}
                onSubjectChange={setSubject}
              />
            )}
            {key === 'profile' && (
              <StepProfile
                formData={formData}
                setFormData={setFormData}
                folderName={folderName}
                setFolderName={setFolderName}
                regenerateFolder={regenerateFolder}
                specialtyInput={specialtyInput}
                setSpecialtyInput={setSpecialtyInput}
              />
            )}
            {key === 'confirm' && (
              <StepConfirm
                grade={grade}
                field={field}      // 🔽 CHANGED
                subject={subject}
                folderName={folderName}
                formData={formData}
              />
            )}
          </>
        )}
      </Wizard>
    </Modal>
  );
}

// ================================================================
// SECTION 11: WIZARD STEPS
// ================================================================

function StepBasics({
  grade, field, subject, subjectsForGrade,
  onGradeChange, onFieldChange, onSubjectChange,
}) {
  return (
    <div className="tb-wizard-step">
      <p className="tb-wizard-intro">
        انتخاب کنید که می‌خواهید معلم کدام کتاب در کدام پایه باشید.
      </p>

      {/* 🔽 CHANGED: گرید کامل ۱-۱۲ */}
      <div className="tb-field">
        <label className="tb-label">پایه تحصیلی <span className="tb-req">*</span></label>
        <div className="tb-grade-grid">
          {GRADE_LIST.map((g) => (
            <button
              key={g.id}
              type="button"
              className={`tb-grade-btn ${grade === g.id ? 'active' : ''}`}
              onClick={() => onGradeChange(g.id)}
            >
              <span className="tb-grade-num">{g.id}</span>
              <span className="tb-grade-label">{g.title}</span>
            </button>
          ))}
        </div>
      </div>

      {/* 🔽 CHANGED: dropdown رشته — فقط برای پایه‌های ۱۰-۱۲ */}
      {grade && curriculumNeedsField(grade) && (
        <div className="tb-field">
          <label className="tb-label">رشته تحصیلی <span className="tb-req">*</span></label>
          <div className="tb-subject-grid">
            {FIELD_LIST.map((f) => (
              <button
                key={f.id}
                type="button"
                className={`tb-subject-btn ${field === f.id ? 'active' : ''}`}
                onClick={() => onFieldChange(f.id)}
              >
                <span className="tb-subject-emoji">🎓</span>
                <span className="tb-subject-label">{f.title}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="tb-field">
        <label className="tb-label">کتاب درسی <span className="tb-req">*</span></label>
        {!grade ? (
          <div className="tb-hint tb-hint-block">
            ابتدا پایه را انتخاب کنید
          </div>
        ) : curriculumNeedsField(grade) && !field ? (
          <div className="tb-hint tb-hint-block">
            ابتدا رشته را انتخاب کنید
          </div>
        ) : subjectsForGrade.length === 0 ? (
          <div className="tb-hint tb-hint-block">
            کتابی برای این انتخاب یافت نشد
          </div>
        ) : (
          <div className="tb-subject-grid">
            {subjectsForGrade.map((s) => (
              <button
                key={s}
                type="button"
                className={`tb-subject-btn ${subject === s ? 'active' : ''}`}
                onClick={() => onSubjectChange(s)}
              >
                <span className="tb-subject-emoji">{SUBJECT_EMOJI[s] || '📘'}</span>
                <span className="tb-subject-label">{SUBJECT_LABELS[s]}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// 🔽 CHANGED: emojis برای همه‌ی ۲۹ درس (شامل متوسطه اول و دوم)
const SUBJECT_EMOJI = {
  // ابتدایی و متوسطه اول
  riazi: '📐', farsi: '📖', olum: '🔬', ghoran: '🕌',
  hedye: '🌙', motaleat: '🌍', tafakor: '🧠', kar: '⚙️',
  payam: '🕊️', farhang: '🎨', arabi: '📜', english: '🌐',
  // متوسطه دوم — مشترک
  din: '🕌',
  // ریاضی و فیزیک
  hendese: '📏', hesaban: '🧮', amar_ehtemal: '📊',
  riazi_gosaste: '🔢', fizik: '⚡', shimi: '⚗️',
  // تجربی
  zist: '🧬',
  // انسانی
  riazi_amar: '📈', olon_fonon: '📚', tarikh: '🏛️',
  joghrafia: '🗺️', jamee: '👥', eqtesad: '💹',
  manteq: '🤔', ravanshenasi: '🧠', falsafe: '💭',
  // ویژه
  joghrafia_iran: '🗺️', tarikh_moaser: '📖',
  hoviat: '🆔', salamat: '🩺',
};

function StepProfile({
  formData, setFormData, folderName, setFolderName, regenerateFolder,
  specialtyInput, setSpecialtyInput,
}) {
  const { upload: uploadImage } = useImageUpload();

  const addSpecialty = () => {
    const v = specialtyInput.trim();
    if (!v || formData.specialties.includes(v)) return;
    setFormData((f) => ({ ...f, specialties: [...f.specialties, v] }));
    setSpecialtyInput('');
  };

  const removeSpecialty = (s) => {
    setFormData((f) => ({ ...f, specialties: f.specialties.filter((x) => x !== s) }));
  };

  return (
    <div className="tb-wizard-step">
      <p className="tb-wizard-intro">
        اطلاعات حرفه‌ای شما به دانش‌آموزان کمک می‌کند انتخاب بهتری داشته باشند.
      </p>

      <div className="tb-wizard-row">
        <div className="tb-field">
          <label className="tb-label">نام و نام خانوادگی <span className="tb-req">*</span></label>
          <input
            className="tb-input"
            value={formData.fullName}
            onChange={(e) => setFormData((f) => ({ ...f, fullName: e.target.value }))}
            maxLength={100}
            placeholder="مثلاً: مهدی علی‌آبادی"
          />
        </div>

        <div className="tb-field tb-field-narrow">
          <label className="tb-label">سابقه (سال)</label>
          <input
            type="number"
            className="tb-input"
            value={formData.experienceYears}
            min={0}
            max={80}
            onFocus={(e) => e.target.select()}
            onChange={(e) => setFormData((f) => ({
              ...f,
              experienceYears: Math.max(0, Math.min(80, parseInt(e.target.value) || 0)),
            }))}
          />
        </div>
      </div>

      <div className="tb-field">
        <label className="tb-label">عکس پروفایل</label>
        <ImageUploader
          value={formData.avatarBlobId}
          onChange={(blobId) => setFormData((f) => ({ ...f, avatarBlobId: blobId }))}
          label=""
          aspect="1/1"
          refType="avatar"
          hint="حداکثر ۲ مگابایت — پیشنهاد: ۵۰۰×۵۰۰ پیکسل"
        />
      </div>

      <div className="tb-field">
        <label className="tb-label">بیوگرافی</label>
        <textarea
          className="tb-textarea"
          rows={4}
          maxLength={2000}
          value={formData.bio}
          onChange={(e) => setFormData((f) => ({ ...f, bio: e.target.value }))}
          placeholder="روش تدریس، سابقه، دستاوردها..."
        />
        <div className="tb-hint">{formData.bio.length} / 2000</div>
      </div>

      <div className="tb-field">
        <label className="tb-label">تخصص‌ها</label>
        <div className="tb-inline-add">
          <input
            className="tb-input"
            value={specialtyInput}
            onChange={(e) => setSpecialtyInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addSpecialty(); } }}
            placeholder="مثلاً: آموزش بازی‌محور"
            maxLength={50}
          />
          <button type="button" className="tb-btn tb-btn-ghost" onClick={addSpecialty}>
            افزودن
          </button>
        </div>
        <div className="tb-chips">
          {formData.specialties.map((s) => (
            <Chip key={s} tone="purple" onRemove={() => removeSpecialty(s)}>
              {s}
            </Chip>
          ))}
        </div>
      </div>

      <div className="tb-field">
        <label className="tb-label">
          شناسه پوشه <span className="tb-req">*</span>
          <span className="tb-label-lock">🔒 پس از انتشار قابل تغییر نیست</span>
        </label>
        <div className="tb-inline-add">
          <input
            className="tb-input tb-input-code"
            value={folderName}
            onChange={(e) => setFolderName(e.target.value.replace(/[^A-Za-z0-9-]/g, ''))}
            maxLength={64}
            dir="ltr"
          />
          <button
            type="button"
            className="tb-btn tb-btn-ghost"
            onClick={regenerateFolder}
            title="تولید مجدد"
          >
            🔁
          </button>
        </div>
        <div className="tb-hint">
          فقط حرف انگلیسی، عدد و خط تیره — مثل: Mahdi-Aliabadi
        </div>
      </div>
    </div>
  );
}

function StepConfirm({ grade, field, subject, folderName, formData }) {
  return (
    <div className="tb-wizard-step">
      <p className="tb-wizard-intro">
        لطفاً اطلاعات زیر را بازبینی کنید. پس از ثبت، درخواست شما برای بررسی مدیر ارسال می‌شود.
      </p>

      <div className="tb-confirm-card">
        <div className="tb-confirm-row">
          <span className="tb-confirm-label">📚 کتاب</span>
          <span className="tb-confirm-value">
            {/* 🔽 CHANGED: نمایش رشته در کنار پایه */}
            {SUBJECT_LABELS[subject]} — {formatGradeField(grade, field)}
          </span>
        </div>
        <div className="tb-confirm-row">
          <span className="tb-confirm-label">👤 نام</span>
          <span className="tb-confirm-value">{formData.fullName}</span>
        </div>
        <div className="tb-confirm-row">
          <span className="tb-confirm-label">🏷️ شناسه پوشه</span>
          <span className="tb-confirm-value tb-confirm-code">{folderName}</span>
        </div>
        <div className="tb-confirm-row">
          <span className="tb-confirm-label">💼 سابقه</span>
          <span className="tb-confirm-value">{formData.experienceYears} سال</span>
        </div>
        {formData.specialties.length > 0 && (
          <div className="tb-confirm-row">
            <span className="tb-confirm-label">⭐ تخصص‌ها</span>
            <div className="tb-chips">
              {formData.specialties.map((s) => <Chip key={s} tone="purple">{s}</Chip>)}
            </div>
          </div>
        )}
        {formData.bio && (
          <div className="tb-confirm-row">
            <span className="tb-confirm-label">📝 بیوگرافی</span>
            <span className="tb-confirm-value tb-confirm-bio">{formData.bio}</span>
          </div>
        )}
      </div>

      <div className="tb-wizard-note">
        ℹ️ پس از تایید مدیر، پروژه‌ی شما ساخته می‌شود و می‌توانید فصل‌ها و درس‌ها را بسازید.
      </div>
    </div>
  );
}